import type {
  ActivateAdminUserRequest,
  AdminLoginAttemptDto,
  AdminUserDetailDto,
  AdminUserListDto,
  AdminUserListParams,
  ChangeAdminUserRoleRequest,
  CreateAdminUserRequest,
  DeactivateAdminUserRequest,
  ResendInvitationRequest,
  SetupPasswordRequest,
  UpdateAdminUserRequest,
} from '../types/admin-user.types';
import httpClient from '@/core/api/createAxiosHttpClient';
import type { PagedResult } from '@/shared/types/paginated.type';

/**
 * Admin Users API Service
 * Handles all admin user management endpoints
 */
export const adminUsersApi = {
  /**
   * List admin users with pagination and optional role filter
   * GET /api/admin/users
   * Auth: SuperAdmin required
   */
  listUsers: async (params: AdminUserListParams = {}) => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', String(params.page));
    if (params.pageSize) queryParams.append('pageSize', String(params.pageSize));
    if (params.status) queryParams.append('status', params.status);
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);

    const queryString = queryParams.toString();
    const url = queryString ? `/admin/users?${queryString}` : '/admin/users';

    return httpClient.get<PagedResult<AdminUserListDto>>(url);
  },

  /**
   * Get admin user detail
   * GET /api/admin/users/:adminUserId
   * Auth: SuperAdmin required
   */
  getUser: async (adminUserId: string) => {
    return httpClient.get<AdminUserDetailDto>(`/admin/users/${adminUserId}`);
  },

  /**
   * Create a new admin user
   * POST /api/admin/users
   * Auth: SuperAdmin required
   */
  createUser: async (data: CreateAdminUserRequest) => {
    return httpClient.post<CreateAdminUserRequest, void>('/admin/users', data);
  },

  /**
   * Update an existing admin user
   * PUT /api/admin/users/{adminUserId}
   * Auth: SuperAdmin required
   */
  updateUser: async (adminUserId: string, data: UpdateAdminUserRequest) => {
    return httpClient.put<UpdateAdminUserRequest>(`/admin/users/${adminUserId}`, data);
  },

  /**
   * Validate password setup token
   * POST /api/admin/auth/password-setup/validate
   * Auth: Public (no authentication required)
   */
  validateToken: async (token: string) => {
    return httpClient.postPublic<{ token: string }, void>(`/admin/auth/password-setup/validate`, {
      token,
    });
  },

  /**
   * Setup password for new admin user
   * POST /api/admin/setup-password
   * Auth: Public (no authentication required)
   */
  setupPassword: async (data: SetupPasswordRequest) => {
    return httpClient.postPublic<SetupPasswordRequest, void>('/admin/auth/password-setup', data);
  },

  /**
   * Activate an admin user
   * PATCH /api/admin/users/{adminUserId}/activate
   */
  activateUser: async (adminUserId: string, data: ActivateAdminUserRequest) => {
    return httpClient.patch<object>(`/admin/users/${adminUserId}/activate`, data);
  },

  /**
   * Deactivate an admin user
   * PATCH /api/admin/users/{adminUserId}/deactivate
   */
  deactivateUser: async (adminUserId: string, data: DeactivateAdminUserRequest) => {
    return httpClient.patch<object>(`/admin/users/${adminUserId}/deactivate`, data);
  },

  /**
   * Resend password setup invitation
   * POST /api/admin/users/{adminUserId}/resend-invitation
   */
  resendInvitation: async (adminUserId: string, data: ResendInvitationRequest) => {
    return httpClient.post<ResendInvitationRequest, void>(
      `/admin/users/${adminUserId}/resend-invitation`,
      data,
    );
  },

  /**
   * Change role of an admin user
   * PATCH /api/admin/users/{adminUserId}/role
   */
  changeUserRole: async (adminUserId: string, data: ChangeAdminUserRoleRequest) => {
    return httpClient.patch<ChangeAdminUserRoleRequest>(`/admin/users/${adminUserId}/role`, data);
  },

  /**
   * Soft-delete an admin user
   * DELETE /api/admin/users/{adminUserId}
   */
  deleteUser: async (adminUserId: string) => {
    return httpClient.delete<void>(`/admin/users/${adminUserId}`);
  },

  /**
   * Get admin user login attempt history (success/fail per attempt)
   * GET /api/admin/users/{adminUserId}/login-attempts
   */
  getLoginAttempts: async (
    adminUserId: string,
    params: { page?: number; pageSize?: number } = {},
  ) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(params.page ?? 1));
    queryParams.append('pageSize', String(params.pageSize ?? 10));
    return httpClient.get<PagedResult<AdminLoginAttemptDto>>(
      `/admin/users/${adminUserId}/login-attempts?${queryParams.toString()}`,
    );
  },
};
